import React, { useMemo, useState, useRef } from 'react';
import './SidebarPopup.css';
import { isMobile } from '@direct-frontend/platform-code';
import { useLogger } from '@direct-frontend/modules-logger';
import { genericMemo, useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';
import { useOverlayTriggerState } from 'react-stately';

import { Popup, PopupAutoFocusProps } from '../../Popup';
import { TooltipContainerContext } from '../../Tooltip/Tooltip';
import { cn } from '../../../lib/css/className/className';
import { Card } from '../../Card';
import { useVStack } from '../../../hooks/useStack';
import { Drawer, DrawerHeader } from '../../Drawer';
import { useSidebarIsNewMobileView } from '../SidebarIsNewMobileViewStateProvider';
import { MenuListContext } from '../../MenuTrigger/utils';
import { SubmenuContext } from '../../MenuTrigger/SubmenuTrigger/utils';
import { useMenuRoot } from '../../MenuTrigger/useMenuRoot';
import { useSidebarCollapsedState } from '../SidebarCollapsedStateProvider';
import { useSidebarPopupState } from '../SidebarPopupStateProvider';

import { SidebarPopupProps } from './types';

const cnSidebarPopup = cn('SidebarPopup');

const popupAutoFocusProps: PopupAutoFocusProps = {
    autoFocus: true,
    contain: false,
    restoreFocus: true,
};

export const SidebarPopup = genericMemo(function SidebarPopup({
    children,
    content,
    'data-testid': dataTestId = 'SidebarPopup',
    height,
    title,
    isDisabled,
    triggerClassName,
    placement = 'right-start',
    logNode,
    id,
    openTrigger: openTriggerProp,
    onClick,
}: SidebarPopupProps) {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const { LogProvider } = useLogger('SidebarPopup', logNode);
    const isNewMobileView = useSidebarIsNewMobileView();

    const { close } = useOverlayTriggerState({});
    const openTrigger = isMobile() ? 'click' : (openTriggerProp ?? 'hover');
    const {
        actions,

        onOpenMenu,
        onCloseMenu,
        onMenuLeave,

        onMouseMove,

        popupOffset,
        openedMenuId,
    } = useSidebarPopupState(openTrigger, id, triggerRef, onClick);
    const { popupRef, contextValue } = useMenuRoot({ close });

    const submenuContextValue = useMemo(
        () => ({
            parentMenuRef: popupRef,
        }),
        [popupRef],
    );

    const { isCollapsed } = useSidebarCollapsedState();

    const isOpen = id === openedMenuId;

    const [triggerMargin, setTriggerMargin] = useState(0);
    useIsomorphicLayoutEffect(() => {
        /**
         * https://stackoverflow.com/questions/8628005/why-does-getcomputedstyle-return-auto-for-pixels-values-right-after-element-cr/8628029
         * Иногда, при получении computed стилей margin может быть auto, на что parseInt будет NaN
         * Для корректного вычисления мы создаем макро-таску на пересчет лейаута
         */
        const timerId = window.setTimeout(() => {
            const marginRightPropertyValue = triggerRef?.current
                ? parseInt(window.getComputedStyle(triggerRef.current).getPropertyValue('margin-right'))
                : 0;
            const offset = Number.isNaN(marginRightPropertyValue) ? 0 : marginRightPropertyValue;

            setTriggerMargin(offset);
        }, 0);

        return () => {
            window.clearTimeout(timerId);
        };
    }, [isCollapsed, triggerRef]);

    const cardVStackClasses = useVStack({
        scrollable: true,
    });

    const handledContent = content instanceof Function ? content?.(onMenuLeave) : content;

    const popupActions = useMemo(() => {
        switch (openTrigger) {
            case 'click':
                return {
                    onClick: onCloseMenu,
                    onMouseLeave: onCloseMenu,
                };
            case 'hover':
                return { onMouseLeave: onCloseMenu };
        }
    }, [openTrigger, onCloseMenu]);

    if (isMobile()) {
        return (
            <LogProvider>
                {isNewMobileView ? (
                    React.cloneElement(children, {
                        ...(!isDisabled && {
                            onClick: actions.onClick,
                            ref: triggerRef,
                        }),
                    })
                ) : (
                    <button
                        className={cnSidebarPopup('trigger', {}, [triggerClassName])}
                        {...(!isDisabled && actions)}
                        ref={triggerRef}
                        onMouseMove={onMouseMove}
                    >
                        {children}
                    </button>
                )}
                <Drawer
                    isOpen={isOpen}
                    onClose={onCloseMenu}
                    header={
                        <DrawerHeader
                            onCancelClick={onCloseMenu}
                            title={title}
                            data-testid={`${dataTestId ?? 'Menu'}.Header`}
                        />
                    }
                >
                    {isOpen && <Card className={cnSidebarPopup('content', [cardVStackClasses])}>{handledContent}</Card>}
                </Drawer>
            </LogProvider>
        );
    }

    return (
        <LogProvider>
            <button
                className={cnSidebarPopup('trigger', {}, [triggerClassName])}
                {...(!isDisabled && actions)}
                ref={triggerRef}
                onMouseMove={onMouseMove}
            >
                {children}
            </button>
            <Popup
                className={cnSidebarPopup('')}
                ref={popupRef}
                isOpen={isOpen}
                strategy="fixed"
                isDismissable
                ariaType="menu"
                triggerRef={triggerRef}
                placement={placement}
                flip="never"
                isModal={false}
                shouldResize={height !== 'max'}
                data-testid={`${dataTestId}.Popup`}
                autoFocusProps={popupAutoFocusProps}
                offset={popupOffset + triggerMargin}
                onMouseEnter={onOpenMenu}
                {...popupActions}
            >
                <MenuListContext.Provider value={contextValue}>
                    <SubmenuContext.Provider value={submenuContextValue}>
                        <TooltipContainerContext.Provider value={popupRef}>
                            {isOpen && (
                                <Card className={cnSidebarPopup('content', [cardVStackClasses])}>{handledContent}</Card>
                            )}
                        </TooltipContainerContext.Provider>
                    </SubmenuContext.Provider>
                </MenuListContext.Provider>
            </Popup>
        </LogProvider>
    );
});
